import { el, awaitRedraw } from "@dhx/ts-common/dom";
import { isEmptyObj } from "@dhx/ts-common/core";
import { Label } from "./helper/label";
import { baseProps, getFormItemCss, getValidationMessage, isVerify } from "../helper";
import { IEventSystem, EventSystem } from "@dhx/ts-common/events";
import {
	ValidationStatus,
	ISelectConfig,
	ItemEvent,
	ISelectEventHandlersMap,
	IOption,
	ISelect,
	ISelectProps,
} from "../types";

export class Select extends Label implements ISelect {
	public config: ISelectConfig;
	public events: IEventSystem<ItemEvent, ISelectEventHandlersMap> = new EventSystem();

	private _isValid = true;
	private _propsItem = [
		"required",
		"validation",
		"icon",
		"label",
		"labelWidth",
		"labelPosition",
		"hiddenLabel",
		"helpMessage",
		"preMessage",
		"successMessage",
		"errorMessage",
	];
	private _props = [...baseProps, ...this._propsItem];

	constructor(container, config: ISelectConfig) {
		super(null, config);

		this._initView(config);
		this._initHandlers();
	}
	destructor() {
		this.events && this.events.clear();
		this.events = this._uid = this._propsItem = this._props = this._isValid = null;

		super._destructor();
		this.unmount();
	}
	setProperties(propertyConfig: ISelectProps): void {
		if (
			!propertyConfig ||
			isEmptyObj(propertyConfig) ||
			!this.events.fire(ItemEvent.beforeChangeProperties, [this.getProperties()])
		)
			return;

		for (const key in propertyConfig) {
			if (this._props.includes(key)) {
				this.config[key] = propertyConfig[key];
			}
		}
		this.events.fire(ItemEvent.afterChangeProperties, [this.getProperties()]);
		this.paint();
	}
	getProperties(): ISelectProps {
		const obj: ISelectProps = {};
		for (const prop of this._props) {
			obj[prop] = this.config[prop];
		}
		return obj;
	}
	show(): void {
		const { value, hidden } = this.config;
		if (!hidden || !this.events.fire(ItemEvent.beforeShow, [value])) {
			return;
		}
		this.config.hidden = false;
		this.events.fire(ItemEvent.afterShow, [value]);
	}
	hide(init?: boolean): void {
		const { value, hidden } = this.config;
		if ((hidden && !init) || !this.events.fire(ItemEvent.beforeHide, [value, init])) {
			return;
		}
		this.config.hidden = true;
		this.events.fire(ItemEvent.afterHide, [value, init]);
	}
	isVisible(): boolean {
		return !this.config.hidden;
	}
	disable(): void {
		this.config.disabled = true;
		this.paint();
	}
	enable(): void {
		this.config.disabled = false;
		this.paint();
	}
	isDisabled(): boolean {
		return this.config.disabled;
	}
	validate(silent = false): boolean {
		const { value, validation } = this.config;

		if (!silent && !this.events.fire(ItemEvent.beforeValidate, [value])) return;
		this._isValid = validation
			? this.config.validation(value)
			: typeof value !== "undefined" && !!String(value).length;
		!silent && this.events.fire(ItemEvent.afterValidate, [value, this._isValid]);

		return this._isValid;
	}
	clearValidate(): void {
		this.config.$validationStatus = ValidationStatus.pre;
		this.paint();
	}
	clear(): void {
		if (this.config.value === this.config.options[0].value) return;
		this.config.value = this.config.options[0].value;
		this.events.fire(ItemEvent.change, [this.getValue()]);
	}
	setValue(value: string | number): void {
		if (
			typeof value === "undefined" ||
			value === this.config.value ||
			this.config.options.findIndex(i => i.value === value) === -1
		)
			return;
		this.config.value = value;
		this.events.fire(ItemEvent.change, [value]);
		isVerify(this.config) && this.validate();
	}
	focus(): void {
		awaitRedraw().then(() => {
			this.getRootView().refs.select.el.focus();
		});
	}
	blur(): void {
		awaitRedraw().then(() => {
			this.getRootView().refs.select.el.blur();
		});
	}
	getValue(): string | number {
		return this.config.value;
	}
	setOptions(options: IOption[]): void {
		if (!options || !options.length) {
			throw new Error(
				"Function argument cannot be empty, for more info check documentation https://docs.dhtmlx.com/suite/form__select.html#addingselect"
			);
		}

		this._checkOptions(options);

		this.config.options = [...options];
		this.config.value = this.config.options[0].value;
		this.events.fire(ItemEvent.changeOptions, [[...options]]);
		this.paint();
	}
	getOptions(): IOption[] {
		return this.config.options;
	}
	protected _initView(config: ISelectConfig): void {
		if (isEmptyObj(config)) {
			throw new Error("Check the configuration is correct");
		}
		this._checkOptions(config.options);

		this.config = {
			type: config.type,
			id: config.id,
			name: config.name,
			disabled: false,
			hidden: false,
			required: false,
			icon: "",
			label: "",
			labelWidth: "",
			labelPosition: "top",
			hiddenLabel: false,
			helpMessage: "",
			preMessage: "",
			successMessage: "",
			errorMessage: "",
			width: "content",
			height: "content",
			options: config.options,
			value: config.options[0].value,
		};

		for (const key in config) {
			if (key !== "id" && key !== "type" && key !== "name") {
				this.config[key] = config[key];
			}
		}

		if (this.config.hidden) {
			awaitRedraw().then(() => {
				this.hide(true);
			});
		}

		this.paint();
	}
	protected _initHandlers() {
		this.events.on(ItemEvent.afterValidate, () => {
			this.config.$validationStatus = this._isValid ? ValidationStatus.success : ValidationStatus.error;
			this.paint();
		});
		this.events.on(ItemEvent.changeOptions, () => {
			this.events.fire(ItemEvent.change, [this.getValue()]);
			isVerify(this.config) && this.validate();
		});
		this.events.on(ItemEvent.change, () => this.paint());
	}
	protected _getHandlers() {
		return {
			onchange: (e: Event) => {
				const value = this.config.options
					.map(o => o.value)
					.find(v => v == (e.target as HTMLSelectElement).value);

				this.config.value = value;
				this.events.fire(ItemEvent.change, [value]);
				isVerify(this.config) && this.validate();
			},
			onblur: () => {
				if (!this._uid) return;
				this.events.fire(ItemEvent.blur, [this.getValue()]);
				this.paint();
			},
			onfocus: () => {
				this.events.fire(ItemEvent.focus, [this.getValue()]);
				this.paint();
			},
		};
	}
	protected _draw() {
		const { id, options, icon, value, label, labelWidth, helpMessage, required, name } = this.config;

		const activeFocus = this.getRootView()?.refs?.select?.el === document.activeElement;

		return el(
			".dhx_form-group",
			{
				class: getFormItemCss(this.config, isVerify(this.config), activeFocus),
			},
			[
				(label || labelWidth || helpMessage) && this._drawLabel(),
				el(".dhx_input__wrapper", {}, [
					el("div.dhx_input__container", {}, [
						el(".dhx_input__icon", {
							class: icon ? icon : "dxi dxi-menu-down",
						}),
						el(
							"select.dhx_select.dhx_input",
							{
								id,
								"data-dhx-id": name || id,
								tabindex: 0,
								class: activeFocus && "dhx_input--focus",
								onchange: this._handlers.onchange,
								onfocus: this._handlers.onfocus,
								onblur: this._handlers.onblur,
								onkeydown: this._handlers.onkeydown,
								_ref: "select",
								required,
							},
							options.length &&
								options.map(option =>
									el(
										"option",
										{
											value: option.value,
											disabled: option.disabled,
											selected: value === option.value,
										},
										option.content
									)
								)
						),
					]),
					getValidationMessage(this.config) &&
						el("span.dhx_input__caption", getValidationMessage(this.config)),
				]),
			]
		);
	}
	private _checkOptions(options: IOption[]) {
		if (options.length === 0) {
			throw new Error(
				"Property options* cannot be empty, for more info check documentation https://docs.dhtmlx.com/suite/form__select.html#addingselect"
			);
		}
		options.forEach(item => {
			if (!item.hasOwnProperty("value") || !item.hasOwnProperty("content")) {
				throw new Error(
					"The object must contain two required properties value and content, for more info check documentation https://docs.dhtmlx.com/suite/form__select.html#addingselect"
				);
			}
		});
	}
}
